import PincodeField, { pincodeLocation, emptyPincodeLocation } from '@/components/PincodeField';
import './startup-profile.css';
import StartupPageTitle from '../../../components/StartupPageTitle';
import { Buildings as PageHeadingIcon } from '@phosphor-icons/react';
import React, { useState, useEffect, useRef } from 'react';
import api, { fileUrl, openAuthenticatedFile } from '@/api';
import { toast } from 'sonner';
import { formatDate } from '@/utils/datetime';
import { ArrowUpRight, Sparkle, Upload, User, Building, MapPin, FileText, Trash, Check, X, Spinner, SealCheck, Eye, EyeSlash, ShieldCheck } from '@phosphor-icons/react';
import ThemeToggle from '../../../components/ThemeToggle';
import UserProfileCard from '../../../components/UserProfileCard';
import PhoneField, { isValidPhoneNumber } from '../../../components/PhoneField';
import { SECTORS } from '@/utils/sector_options';
import StartupBadges, { STARTUP_BADGES } from '../../../components/StartupBadges';

const PROFILE_CARD_FIELDS = {
  basic: ['startup_name', 'one_line_description', 'sector', 'funding_stage', 'business_model'],
  location: ['pincode','hq_state','hq_country','hq_location','hq_city', 'year_founded', 'website', 'founder_phone'],
  badges: ['badges'],
  status: ['team_size', 'fundraising_status', 'current_valuation'],
};

const pickProfileFields = (source, keys) => keys.reduce((acc, key) => {
  acc[key] = source?.[key];
  return acc;
}, {});

const StartupProfile = ({ user }) => {
  const [locationReady,setLocationReady]=useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [accountDirty, setAccountDirty] = useState(false);
  const accountControls = useRef(null);
  const [savedFormData, setSavedFormData] = useState(null);
  const [savingSection, setSavingSection] = useState(null);
  const [startup, setStartup] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingDeck, setUploadingDeck] = useState(false);
  const [removingDeck, setRemovingDeck] = useState(false);
  const [deckInfo, setDeckInfo] = useState(null);
  const [visibilityData, setVisibilityData] = useState(null);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const deckInputRef = useRef(null);
  const [formData, setFormData] = useState({
    startup_name: '',
    one_line_description: '',
    sector: '',
    pincode:'',hq_state:'',hq_country:'',hq_location:'',
    hq_city: '',
    year_founded: new Date().getFullYear(),
    website: '',
    business_model: [],
    funding_stage: '',
    team_size: '',
    fundraising_status: '',
    current_valuation: '',
    founder_phone: '',
    badges: []
  });

  useEffect(() => {
    fetchProfile();
    fetchVisibility();
  }, []);

  const fetchVisibility = async () => {
    try {
      const res = await api.get('/startup/visibility');
      setVisibilityData(res.data);
    } catch (error) {
      console.error('Failed to fetch visibility data', error);
    }
  };

  const toggleDiscoverability = async () => {
    setVisibilitySaving(true);
    try {
      const nextValue = !visibilityData?.discoverability_enabled;
      await api.patch('/startup/profile', { discoverability_enabled: nextValue });
      setVisibilityData((prev) => ({ ...(prev || {}), discoverability_enabled: nextValue }));
      toast.success(nextValue ? 'You are now discoverable by VCs' : 'Your startup is hidden from VC discovery');
    } catch (error) {
      toast.error('Failed to update visibility');
    } finally {
      setVisibilitySaving(false);
    }
  };

  const fetchProfile = async () => {
    setLoadError(false);
    try {
      const response = await api.get('/startup/dashboard');
      const s = response.data.startup;
      setStartup(s);
      const nextFormData = {
        startup_name: s.startup_name || '',
        one_line_description: s.one_line_description || '',
        sector: s.sector || '',
        hq_city:s.hq_city||'',pincode:s.pincode||'',hq_state:s.hq_state||'',hq_country:s.hq_country||'',hq_location:s.hq_location||'',
        year_founded: s.year_founded || new Date().getFullYear(),
        website: s.website || '',
        business_model: s.business_model || [],
        funding_stage: s.funding_stage || '',
        team_size: s.team_size || '',
        fundraising_status: s.fundraising_status || 'Not fundraising',
        current_valuation: s.current_valuation || '',
        // Clear legacy free-text values that aren't valid E.164 so the field
        // falls back to the +91 default and the founder re-enters cleanly.
        founder_phone: isValidPhoneNumber(s.founder_phone || '') ? s.founder_phone : '',
        badges: s.badges || []
      };
      setFormData(nextFormData);
      setSavedFormData(nextFormData);

      // Fetch pitch deck info if exists
      if (s.pitch_deck_file_id) {
        try {
          const deckRes = await api.get(`/files/${s.pitch_deck_file_id}/info`);
          setDeckInfo(deckRes.data);
        } catch { setDeckInfo(null); }
      } else {
        setDeckInfo(null);
      }
    } catch (error) {
      setLoadError(true);
      console.error('Failed to fetch profile', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const isCardDirty = (section) => {
    if (!savedFormData) return false;
    const keys = PROFILE_CARD_FIELDS[section] || [];
    return keys.some((key) => JSON.stringify(formData?.[key]) !== JSON.stringify(savedFormData?.[key]));
  };

  const validateSection = (section) => {
    if (section === 'basic' && (!formData.startup_name.trim() || !formData.one_line_description.trim() || !formData.sector || !formData.funding_stage)) {
      toast.error('Add a company name, description, sector and funding stage');
      return false;
    }
    if (section === 'status' && formData.current_valuation !== '' && (!Number.isFinite(Number(formData.current_valuation)) || Number(formData.current_valuation) < 0)) {
      toast.error('Valuation must be a valid positive amount'); return false;
    }
    if (section === 'location') {
      if ((formData.pincode || formData.hq_city !== savedFormData?.hq_city || formData.pincode !== savedFormData?.pincode) && !locationReady) { toast.error('Wait for a valid pincode location before saving'); return false; }
      if (!Number.isInteger(Number(formData.year_founded)) || Number(formData.year_founded) < 1900 || Number(formData.year_founded) > new Date().getFullYear()) {
        toast.error('Enter a valid founding year'); return false;
      }
      if (formData.website) {
        try { const url = new URL(formData.website); if (!['http:', 'https:'].includes(url.protocol)) throw new Error(); }
        catch { toast.error('Enter a website starting with https:// or http://'); return false; }
      }
      if (formData.founder_phone && !isValidPhoneNumber(formData.founder_phone)) {
        toast.error('Please enter a valid founder phone number');
        return false;
      }
      if (!formData.pincode && formData.hq_city.trim() && !/^[A-Za-z ]+$/.test(formData.hq_city.trim())) {
        toast.error('HQ city may contain letters and spaces only');
        return false;
      }
    }
    return true;
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) { toast.error('Please upload an image file'); return; }

    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/files/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await api.patch('/startup/profile', { logo_url: res.data.file_id });
      setStartup(prev => ({ ...prev, logo_url: res.data.file_id }));
      window.dispatchEvent(new CustomEvent('founderboard:startup-profile-updated', { detail: { logo_url: res.data.file_id } }));
      toast.success('Logo updated!');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeckUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!/\.(pdf|pptx?)$/i.test(file.name)) { toast.error('Upload a PDF or PowerPoint file'); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error('File must be under 25MB'); return; }

    setUploadingDeck(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/files/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await api.patch('/startup/profile', { pitch_deck_file_id: res.data.file_id });
      setStartup(prev => ({ ...prev, pitch_deck_file_id: res.data.file_id }));
      try { const info = await api.get(`/files/${res.data.file_id}/info`); setDeckInfo(info.data); }
      catch { setDeckInfo({id:res.data.file_id, filename:file.name, size:file.size}); }
      toast.success('Pitch deck uploaded!');
    } catch (error) {
      toast.error('Failed to upload pitch deck');
    } finally {
      setUploadingDeck(false);
      if (deckInputRef.current) deckInputRef.current.value = '';
    }
  };

  const handleViewDeck = async () => {
  try {
    await openAuthenticatedFile(deckInfo.id || startup.pitch_deck_file_id);
  } catch (error) {
    console.error('Failed to open pitch deck', error);
    toast.error('Failed to open pitch deck');
  }
};

  const handleRemoveDeck = async () => {
    if (removingDeck || uploadingDeck) return;
    setRemovingDeck(true);
    try {
      await api.patch('/startup/profile', { pitch_deck_file_id: null });
      setDeckInfo(null);
      setStartup(prev => ({ ...prev, pitch_deck_file_id: null }));
      toast.success('Pitch deck removed');
    } catch (error) {
      toast.error('Failed to remove deck');
    } finally { setRemovingDeck(false); }
  };

  const toggleBusinessModel = (model) => {
    setFormData(prev => ({
      ...prev,
      business_model: prev.business_model.includes(model)
        ? prev.business_model.filter(m => m !== model)
        : [...prev.business_model, model]
    }));
  };

  const toggleBadge = (badgeId) => {
    setFormData(prev => {
      const badges = prev.badges.includes(badgeId)
        ? prev.badges.filter(id => id !== badgeId)
        : [...prev.badges, badgeId];

      return { ...prev, badges };
    });
  };

  useEffect(() => {
    const hasEdits = savedFormData && Object.keys(PROFILE_CARD_FIELDS).some(section => isCardDirty(section));
    if (!hasEdits && !accountDirty) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [formData, savedFormData, accountDirty]);

  const avatarUrl = startup?.logo_url ? fileUrl(startup.logo_url) : null;

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };


  const dirtySections = Object.keys(PROFILE_CARD_FIELDS).filter(isCardDirty);
  const missingEssentials = [
    ['startup_name', 'Company name'], ['one_line_description', 'One-line description'],
    ['sector', 'Sector'], ['funding_stage', 'Funding stage'], ['hq_city', 'Headquarters'],
  ].filter(([key]) => !formData[key]);
  const saveAll = async () => {
    if (savingSection || (!dirtySections.length && !accountDirty)) return;
    if (!dirtySections.every(validateSection)) return;
    if (accountDirty && !accountControls.current?.validate()) return;
    const payload = pickProfileFields(formData, dirtySections.flatMap(section => PROFILE_CARD_FIELDS[section]));
    setSavingSection('all');
    try {
      if (dirtySections.length) await api.patch('/startup/profile', payload);
      setSavedFormData(prev => ({...prev, ...payload}));
      setStartup(prev => ({...prev, ...payload}));
      window.dispatchEvent(new CustomEvent('founderboard:startup-profile-updated', {detail:payload}));
      if (accountDirty && !await accountControls.current?.save()) return;
      toast.success('All changes saved');
    } catch { toast.error('Could not save changes. Your edits are still here.'); }
    finally { setSavingSection(null); }
  };
  const discardAll = () => { if (savingSection) return; if (savedFormData) setFormData({...savedFormData}); accountControls.current?.discard(); };
  const safeWebsite = /^https?:\/\//i.test(formData.website || '') ? formData.website : null;
  const tabs = [['company','Company'],['materials','Materials'],['visibility','Visibility'],['account','Account']];

  if (loading) return <div className="fb-company-profile"><div className="profile-loading" role="status"><Spinner size={24} className="animate-spin" /> Loading company profile…</div></div>;
  if (loadError) return <div className="fb-company-profile"><div className="profile-loading" role="alert"><p>Your company profile couldn’t load.</p><button className="profile-primary" onClick={() => {setLoading(true); fetchProfile();}}>Try again</button></div></div>;

  return <div className="fb-company-profile" data-testid="company-profile-page">
    <header className="startup-page-header profile-page-header">
      <div><StartupPageTitle icon={PageHeadingIcon}>Company Profile</StartupPageTitle><p>Shape how your company appears across FounderBoard.</p></div>
      <div className="profile-header-actions"><span className={dirtySections.length || accountDirty ? 'profile-edit-state is-dirty' : 'profile-edit-state'} aria-live="polite">{dirtySections.length || accountDirty ? 'Unsaved changes' : 'All changes saved'}</span>
      </div>
    </header>
    <div className="profile-company-banner">
      <div className="profile-banner-logo">{avatarUrl ? <img src={avatarUrl} alt="Company logo" onError={e => {e.currentTarget.style.display='none';}} /> : <Building size={30} weight="duotone" />}</div>
      <div className="profile-banner-copy"><span className="profile-eyebrow">YOUR COMPANY WORKSPACE</span><h2>{formData.startup_name || 'Your company'}</h2><p>{formData.one_line_description || 'A clear description makes your company easier to understand.'}</p>
        <div className="profile-banner-tags">{[formData.sector,formData.funding_stage,formData.hq_city].filter(Boolean).map(value => <span key={value}>{value}</span>)}</div>
      </div>
      <button type="button" className="profile-secondary" onClick={() => setActiveTab('visibility')}><ShieldCheck size={16} /> {visibilityData ? visibilityData.discoverability_enabled ? 'Discoverable' : 'Private discovery' : 'Visibility settings'}</button>
    </div>
    <nav className="profile-tabs" role="tablist" aria-label="Company profile sections">{tabs.map(([id,label]) => <button type="button" role="tab" id={`profile-tab-${id}`} aria-selected={activeTab===id} aria-controls={`profile-panel-${id}`} key={id} tabIndex={activeTab===id ? 0 : -1} onKeyDown={event => {
        const keys=['ArrowLeft','ArrowRight','Home','End']; if (!keys.includes(event.key)) return;
        event.preventDefault(); const index=tabs.findIndex(([key])=>key===id);
        const next=event.key==='Home'?0:event.key==='End'?tabs.length-1:(index+(event.key==='ArrowRight'?1:-1)+tabs.length)%tabs.length;
        setActiveTab(tabs[next][0]); document.getElementById(`profile-tab-${tabs[next][0]}`)?.focus();
      }} onClick={() => setActiveTab(id)} className={activeTab===id ? 'is-active' : ''}>{label}{id==='company' && dirtySections.length>0 && <span className="profile-tab-dot" aria-label="Unsaved edits" />}</button>)}</nav>
    <div className="profile-workspace">
      <main className="profile-editors">
        <div role="tabpanel" id="profile-panel-company" aria-labelledby="profile-tab-company" hidden={activeTab!=='company'}>
{/* Basic Information */}
        <div className="bg-surface border border-border rounded-[10px] p-4 sm:p-6 mb-6" data-testid="basic-info-section">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4 flex items-center gap-2">
            <Building size={20} weight="bold" />
            Basic Information
          </h3>

          <div className="space-y-4">
            <div>
              <label htmlFor="company-name-input" className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Company Name *</label>
              <input id="company-name-input"
                type="text"
                value={formData.startup_name}
                onChange={(e) => setFormData({ ...formData, startup_name: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-[#10b981]"
                data-testid="company-name-input"
              />
            </div>

            <div>
              <label htmlFor="description-input" className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">One-line Description * (max 120 chars)</label>
              <input id="description-input"
                type="text"
                value={formData.one_line_description}
                onChange={(e) => setFormData({ ...formData, one_line_description: e.target.value })}
                maxLength={120}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-[#10b981]"
                data-testid="description-input"
              />
              <div className="text-[11px] text-text-muted mt-1">{formData.one_line_description?.length || 0}/120</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sector-select" className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Sector *</label>
              <select id="sector-select"
                  value={formData.sector}
                  onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-[#10b981]"
                  data-testid="sector-select"
                >
                  <option value="">Select sector</option>
                  {SECTORS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="stage-select" className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Funding Stage *</label>
              <select id="stage-select"
                  value={formData.funding_stage}
                  onChange={(e) => setFormData({ ...formData, funding_stage: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-[#10b981]"
                  data-testid="stage-select"
                >
                  <option value="">Select stage</option>
                  <option value="Bootstrapped">Bootstrapped</option>
                  <option value="Pre-seed">Pre-seed</option>
                  <option value="Seed">Seed</option>
                  <option value="Pre-Series A">Pre-Series A</option>
                  <option value="Series A">Series A</option>
                  <option value="Series B">Series B+</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Business Model (select all that apply)</label>
              <div className="flex flex-wrap gap-2">
                {['Subscription', 'Transactional', 'SaaS', 'Marketplace', 'Services', 'Freemium'].map((model) => (
                  <button
                    key={model}
                    type="button"
                    onClick={() => toggleBusinessModel(model)}
                    aria-pressed={formData.business_model?.includes(model)}
                    className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                      formData.business_model?.includes(model)
                        ? 'bg-[#10b981] border-[#10b981] text-white'
                        : 'bg-background border-border text-text-secondary hover:border-[#10b981]/50'
                    }`}
                    data-testid={`business-model-${model.toLowerCase()}`}
                  >
                    {model}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Location & Year */}
        <div className="bg-surface border border-border rounded-[10px] p-4 sm:p-6 mb-6" data-testid="location-section">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4 flex items-center gap-2">
            <MapPin size={20} weight="bold" />
            Location & Timeline
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <PincodeField label="Headquarters pincode" value={formData.pincode} location={formData.hq_location||formData.hq_city} onChange={pin=>setFormData(current=>({...current,...emptyPincodeLocation(pin)}))} onResolve={location=>setFormData(current=>({...current,...pincodeLocation(location)}))} onStatusChange={setLocationReady} id="city-select" testId="city-select" disabled={!!savingSection}/>
            </div>

            <div>
              <label htmlFor="year-founded-input" className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Year Founded *</label>
              <input id="year-founded-input"
                type="number"
                value={formData.year_founded}
                onChange={(e) => setFormData({ ...formData, year_founded: e.target.value === '' ? '' : Number(e.target.value) })}
                min="1900"
                max={new Date().getFullYear()}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary font-mono focus:outline-none focus:border-[#10b981]"
                data-testid="year-founded-input"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="website-input" className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Website</label>
              <input id="website-input"
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://yourcompany.com"
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-[#10b981]"
                data-testid="website-input"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Founder Phone</label>
              <PhoneField
                value={formData.founder_phone}
                onChange={(v) => setFormData({ ...formData, founder_phone: v })}
                placeholder="98xxx xxxxx"
                data-testid="founder-phone-input" aria-label="Founder phone"
              />
              <p className="text-[11px] text-text-muted mt-1">Visible to VCs in your portfolio</p>
            </div>
          </div>
        </div>

        {/* Current Status */}
        <div className="bg-surface border border-border rounded-[10px] p-4 sm:p-6 mb-6" data-testid="status-section">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">Current Status</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="team-size-select" className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Team Size</label>
              <select id="team-size-select"
                value={formData.team_size}
                onChange={(e) => setFormData({ ...formData, team_size: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-[#10b981]"
                data-testid="team-size-select"
              >
                <option value="">Select size</option>
                <option value="Just me">Just me (solo)</option>
                <option value="2-5">2-5</option>
                <option value="6-15">6-15</option>
                <option value="16-30">16-30</option>
                <option value="50+">50+</option>
              </select>
            </div>
            <div>
              <label htmlFor="fundraising-status-select" className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Fundraising Status</label>
              <select id="fundraising-status-select"
                value={formData.fundraising_status}
                onChange={(e) => setFormData({ ...formData, fundraising_status: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-[#10b981]"
                data-testid="fundraising-status-select"
              >
                <option value="Not fundraising">Not fundraising</option>
                <option value="Yes, actively fundraising">Yes, actively fundraising</option>
                <option value="Planning to raise soon">Planning to raise in next 6 months</option>
                <option value="Just closed">Just closed a round</option>
              </select>
            </div>
            <div>
              <label htmlFor="current-valuation-input" className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Current Valuation (Rs.)</label>
              <input id="current-valuation-input"
                type="number"
                value={formData.current_valuation}
                onChange={(e) => setFormData({ ...formData, current_valuation: e.target.value ? parseFloat(e.target.value) : '' })}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-[#10b981] font-mono"
                placeholder="e.g. 50000000 for 5Cr"
                data-testid="current-valuation-input"
              />
              <p className="text-[10px] text-text-muted mt-1">Self-reported. Used for MoIC calculation by your investors.</p>
            </div>
          </div>
        </div>

        {/* Startup Badges */}
        <div className="bg-surface border border-border rounded-[10px] p-4 sm:p-6 mb-6" data-testid="startup-badges-section">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-5">
            <div>
              <h3 className="font-heading font-semibold text-lg text-text-primary flex items-center gap-2">
                <SealCheck size={20} weight="bold" />
                Startup Badges
              </h3>
              <p className="text-xs text-text-muted mt-1">Self-declared badges appear alongside your startup across FounderBoard. Claim only badges that accurately describe your company.</p>
            </div>
            <StartupBadges badges={formData.badges} size="xs" className="sm:justify-end sm:max-w-[300px]" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {STARTUP_BADGES.map(({ id, label, description, icon: Icon, emoji, classes }) => {
              const selected = formData.badges.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggleBadge(id)}
                  className={`text-left rounded-lg border p-4 transition-all ${
                    selected
                      ? 'border-[#10b981]/60 bg-[#10b981]/5 ring-1 ring-[#10b981]/20'
                      : 'border-border bg-background hover:border-[#10b981]/35'
                  }`}
                  data-testid={`claim-badge-${id}`}
                  aria-pressed={selected}
                >
                  <div className="flex items-start gap-3">
                    <span className={`w-11 h-11 rounded-full border flex items-center justify-center flex-shrink-0 ${classes}`}>
                      {emoji ? (
                        <span className="text-xl leading-none" aria-hidden="true">{emoji}</span>
                      ) : (
                        <Icon size={21} weight="bold" />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm text-text-primary">{label}</span>
                        <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${selected ? 'bg-[#10b981] border-[#10b981] text-white' : 'border-border text-transparent'}`}>
                          <Check size={12} weight="bold" />
                        </span>
                      </div>
                      <p className="text-xs text-text-muted mt-1 leading-relaxed">{description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        
        </div>
        <div role="tabpanel" id="profile-panel-materials" aria-labelledby="profile-tab-materials" hidden={activeTab!=='materials'}>
          <div className="profile-section-intro"><h2>Your company’s visual identity</h2><p>A recognizable logo and current deck make a stronger first impression.</p></div>
{/* Avatar Section */}
        <div className="bg-surface border border-border rounded-[10px] p-4 sm:p-6 mb-6" data-testid="avatar-section">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">Company Logo</h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="w-24 h-24 rounded-lg overflow-hidden flex items-center justify-center bg-[#10b981]/10 border border-border">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Company logo"
                  className="w-full h-full object-cover"
                  data-testid="avatar-image"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <span
                className="text-[#10b981] font-mono font-bold text-3xl"
                style={{ display: avatarUrl ? 'none' : 'flex' }}
                data-testid="avatar-fallback"
              >
                {formData.startup_name?.substring(0, 2).toUpperCase() || 'ST'}
              </span>
            </div>
            <div>
              <label className={`inline-flex items-center gap-2 px-4 py-2 bg-[#10b981] text-white rounded-lg cursor-pointer hover:bg-[#10b981]/90 transition-colors ${uploadingAvatar ? 'opacity-50 pointer-events-none' : ''}`}>
                <Upload size={16} weight="bold" />
                <span className="text-sm font-medium">{uploadingAvatar ? 'Uploading...' : 'Upload logo'}</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  data-testid="avatar-upload"
                  disabled={uploadingAvatar}
                />
              </label>
              <p className="text-[11px] text-text-muted mt-2">PNG, JPG, WebP up to 5MB</p>
            </div>
          </div>
        </div>

        {/* Pitch Deck Section */}
        <div className="bg-surface border border-border rounded-[10px] p-4 sm:p-6 mb-6" data-testid="pitch-deck-section">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-1">Pitch Deck</h3>
          <p className="text-[12px] text-text-muted mb-4">Upload your latest pitch deck. VCs who connect with you can request access.</p>

          {deckInfo ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 bg-background border border-border rounded-lg">
              <div className="w-10 h-10 bg-[#10b981]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText size={20} className="text-[#10b981]" weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-text-primary font-medium truncate" data-testid="deck-filename">
                  {deckInfo.filename}
                </div>
                <div className="text-[11px] text-text-muted">
                  {formatFileSize(deckInfo.size)}
                  {deckInfo.created_at && ` · Uploaded ${formatDate(deckInfo.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}`}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleViewDeck}
                  className="px-3 py-1.5 text-[12px] text-text-secondary border border-border rounded-lg hover:text-text-primary hover:border-[#10b981]/50 transition-colors"
                  data-testid="deck-view-btn"
                >
                  View
                </button>
                <label className={`px-3 py-1.5 text-[12px] text-text-secondary border border-border rounded-lg cursor-pointer ${uploadingDeck || removingDeck ? 'opacity-50 pointer-events-none' : ''}`}>
                  Replace
                  <input
                    type="file"
                    accept=".pdf,.pptx,.ppt"
                    onChange={handleDeckUpload}
                    className="hidden"
                    ref={deckInputRef}
                    data-testid="deck-replace-input" disabled={uploadingDeck || removingDeck}
                  />
                </label>
                <button
                  onClick={handleRemoveDeck}
                  className="p-1.5 text-danger hover:bg-danger/10 rounded-lg transition-colors"
                  data-testid="deck-remove-btn" aria-label="Remove pitch deck" disabled={uploadingDeck || removingDeck}
                >
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>
          ) : (
            <label className={`flex flex-col items-center justify-center p-8 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-[#10b981]/30 transition-colors ${uploadingDeck || removingDeck ? 'opacity-50 pointer-events-none' : ''}`}>
              <FileText size={32} className="text-text-muted mb-2" weight="light" />
              <span className="text-[13px] text-text-primary font-medium">
                {uploadingDeck ? 'Uploading...' : 'Choose your pitch deck'}
              </span>
              <span className="text-[11px] text-text-muted mt-1">PDF, PPT or PPTX up to 25MB</span>
              <input
                type="file"
                accept=".pdf,.pptx,.ppt"
                onChange={handleDeckUpload}
                className="hidden"
                ref={deckInputRef}
                data-testid="deck-upload-input"
                disabled={uploadingDeck || removingDeck}
              />
            </label>
          )}
        </div>

        
        </div>
        <div role="tabpanel" id="profile-panel-visibility" aria-labelledby="profile-tab-visibility" hidden={activeTab!=='visibility'}>
{/* Visibility Section */}
        <div className="bg-surface border border-border rounded-[10px] p-4 sm:p-6 mb-6" data-testid="profile-visibility-card">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${visibilityData?.discoverability_enabled ? 'bg-[#10b981]/15 text-[#10b981]' : 'bg-background text-text-muted'}`}>
                {visibilityData?.discoverability_enabled
                  ? <Eye size={20} weight="bold" />
                  : <EyeSlash size={20} weight="bold" />}
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-text-primary">Visibility</h3>
                <p className="mt-1 text-[12px] text-text-muted">
                  Control whether VCs can discover your startup and understand what information is shared before you connect.
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2.5 self-start sm:self-center">
              <span className={`min-w-[72px] text-right text-[11px] font-semibold leading-none ${visibilityData?.discoverability_enabled ? 'text-[#10b981]' : 'text-text-muted'}`}>
                {visibilityData?.discoverability_enabled ? 'Discoverable' : 'Hidden'}
              </span>
              <button
                type="button"
                onClick={toggleDiscoverability}
                disabled={visibilitySaving || !visibilityData}
                aria-pressed={!!visibilityData?.discoverability_enabled}
                aria-label="Toggle startup discoverability"
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full p-[3px] transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#10b981]/35 ${visibilityData?.discoverability_enabled ? 'bg-[#10b981]' : 'bg-border'} ${visibilitySaving || !visibilityData ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                data-testid="discoverability-toggle"
              >
                <span className={`block h-[22px] w-[22px] rounded-full bg-white shadow-sm transition-transform duration-200 ease-out ${visibilityData?.discoverability_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>

          <div className="mt-5 border-t border-border pt-4">
            <div className="mb-3 flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#10b981]" weight="bold" />
              <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-text-secondary">What VCs can see</div>
            </div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              {[
                { label: 'Company name, description, sector & stage', visible: true },
                { label: 'Team size, location & fundraising status', visible: true },
                { label: 'MRR growth direction & runway range', visible: !!visibilityData?.discoverability_enabled },
                { label: 'Pitch deck, if uploaded', visible: !!visibilityData?.discoverability_enabled },
                { label: 'Exact revenue, cash & detailed P&L', visible: false },
                { label: 'Founder notes & private monthly updates', visible: false },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-2 text-[11px]">
                  <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${item.visible ? 'bg-[#10b981]' : 'bg-danger'}`} />
                  <span className={item.visible ? 'text-text-secondary' : 'text-text-muted'}>{item.label}</span>
                  {!item.visible && <span className="ml-auto shrink-0 text-[9px] text-danger">Hidden</span>}
                </div>
              ))}
            </div>
            <p className="mt-4 text-[10px] leading-4 text-text-muted">
              Full financial data is only shared after you accept a VC portfolio invite.
            </p>
          </div>
        </div>

        
        </div>
        <div role="tabpanel" id="profile-panel-account" aria-labelledby="profile-tab-account" hidden={activeTab!=='account'}><UserProfileCard user={user} externalActions controlsRef={accountControls} onDirtyChange={setAccountDirty} /><ThemeToggle /></div>
      </main>
      <aside className="profile-side-panel">
        <section className="profile-preview" aria-label="Company card preview">
          <div className="profile-preview-heading"><Eye size={16} /><span>Company card preview</span>{dirtySections.length>0 && <small>Draft</small>}</div>
          <div className="profile-preview-logo">{avatarUrl ? <img src={avatarUrl} alt="" /> : <Building size={28} weight="duotone" />}</div>
          <h3>{formData.startup_name || 'Your company name'}</h3><p>{formData.one_line_description || 'Your company’s one-line description will appear here.'}</p>
          <div className="profile-preview-tags">{[formData.sector,formData.funding_stage].filter(Boolean).map(value => <span key={value}>{value}</span>)}</div>
          <StartupBadges badges={formData.badges} size="xs" />
          <dl><div><dt>Headquarters</dt><dd>{formData.hq_city || 'Not added'}</dd></div><div><dt>Founded</dt><dd>{formData.year_founded || 'Not added'}</dd></div><div><dt>Team</dt><dd>{formData.team_size || 'Not added'}</dd></div><div><dt>Fundraising</dt><dd>{formData.fundraising_status || 'Not added'}</dd></div></dl>
          {safeWebsite && <a href={safeWebsite} target="_blank" rel="noopener noreferrer">Visit website <ArrowUpRight size={14} /></a>}
          <p className="profile-preview-note">A preview of your company information. What others can access depends on your visibility settings.</p>
        </section>
        <section className="profile-readiness"><div className="profile-readiness-title"><Sparkle size={18} weight="fill" /><h3>{missingEssentials.length ? 'Make a stronger impression' : 'Your essentials are in place'}</h3></div>
          <p>{missingEssentials.length ? 'Give investors the context they need to understand your company.' : 'Keep your company details and pitch deck current as your startup grows.'}</p>
          {missingEssentials.slice(0,3).map(([key,label]) => <button key={key} type="button" onClick={() => {setActiveTab('company'); window.setTimeout(() => {const el=document.querySelector(`[data-testid="${({startup_name:'company-name-input',one_line_description:'description-input',sector:'sector-select',funding_stage:'stage-select',hq_city:'city-select'})[key]}"]`);el?.scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'center'});el?.focus({preventScroll:true});},0);}}><span>+</span> Add {label.toLowerCase()}</button>)}
          {!deckInfo && <button type="button" onClick={() => setActiveTab('materials')}><FileText size={14} /> Upload your pitch deck</button>}
        </section>
      </aside>
    </div>
    {(dirtySections.length>0 || accountDirty) && <div className="profile-save-dock"><div><strong>Your edits are ready to save</strong><span>Save to update your company across FounderBoard.</span></div><button type="button" className="profile-secondary" onClick={discardAll} disabled={!!savingSection}>Discard changes</button><button type="button" className="profile-primary" onClick={saveAll} disabled={!!savingSection}>{savingSection ? 'Saving…' : 'Save all changes'}</button></div>}
  </div>;
};

export default StartupProfile;
